import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { Typography } from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { getWarehouseAutoSavingsDataV2 } from "@/helpers/apis";
import { GET_WAREHOUSE_AUTO_SAVINGS } from "../../constants";
import { useAppState } from "@/modules/app/useAppContext";
import {
  getDateByStartOfDayWithoutTimeoffset,
  getDateByEndOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { formatCost } from "@/helpers/utils";

interface WarehouseSavingsProps {
  warehouseRK: string;
  startDate: Date;
  endDate: Date;
}

const WarehouseSavings = ({ warehouseRK, startDate, endDate }: WarehouseSavingsProps) => {
  const { instance_id } = useParams();
  const { currency } = useAppState();
  const [aggregation] = useState("day");

  const [realizedSavings, setRealizedSavings] = useState({
    min_value: 0,
    max_value: 0,
  });
  const [estimatedSavings, setEstimatedSavings] = useState(0);

  const { data: savingsData, isLoading } = useQuery({
    queryKey: [
      GET_WAREHOUSE_AUTO_SAVINGS,
      instance_id,
      warehouseRK,
      startDate,
      endDate,
      aggregation,
    ],
    queryFn: () => {
      if (!instance_id) {
        throw new Error("Instance ID is required");
      }
      const start = getDateByStartOfDayWithoutTimeoffset(startDate);
      const end = getDateByEndOfDayWithoutTimeoffset(endDate);
      if (!start || !end) {
        throw new Error("Invalid date range");
      }
      return getWarehouseAutoSavingsDataV2(instance_id, warehouseRK, {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        aggregation,
      });
    },
    enabled: !!instance_id && !!warehouseRK,
  });

  // Handle successful savings data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (savingsData) {
      const realizedSaving = savingsData?.top_info?.realized_saving;
      if (realizedSaving) {
        setRealizedSavings({
          min_value:
            typeof realizedSaving.min_value === "string"
              ? parseFloat(realizedSaving.min_value)
              : realizedSaving.min_value || 0,
          max_value:
            typeof realizedSaving.max_value === "string"
              ? parseFloat(realizedSaving.max_value)
              : realizedSaving.max_value || 0,
        });
      }

      const estimatedValue = savingsData?.top_info?.estimated_annual_savings?.value;
      setEstimatedSavings(
        typeof estimatedValue === "string" ? parseFloat(estimatedValue) : estimatedValue || 0
      );
    }
  }, [savingsData]);

  const formatRealizedSavings = () => {
    if (realizedSavings.min_value === realizedSavings.max_value) {
      return formatCost(realizedSavings.min_value, currency);
    }
    return `${formatCost(realizedSavings.min_value, currency)} - ${formatCost(realizedSavings.max_value, currency)}`;
  };

  return (
    <Stack direction="row" gap="8xl" className="al-my-4">
      <Stack direction="column" gap="xs">
        <Typography variant="body" size="xs" className="al-text-muted-foreground">
          Realised Savings
        </Typography>
        {isLoading ? (
          <Spinner />
        ) : (
          <Typography className="al-text-blue-600">{formatRealizedSavings()}</Typography>
        )}
      </Stack>

      <Stack direction="column" gap="xs">
        <Typography variant="body" size="xs" className="al-text-muted-foreground">
          Estimated Annual Savings
        </Typography>
        {isLoading ? (
          <Spinner />
        ) : (
          <Typography className="al-text-green-600">{formatCost(estimatedSavings, currency)}</Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default WarehouseSavings;
